import { create } from 'zustand'

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
}

export interface ErrorLogEntry {
  id: string
  message: string
  timestamp: number // epoch ms
  url: string      // где произошла ошибка
  source?: string  // опциональный тег: 'toast' | 'network' | 'scan' и т.д.
}

const ERROR_HISTORY_KEY = 'truemark.errorHistory.v1'
const ERROR_HISTORY_LIMIT = 200

function loadErrorHistory(): ErrorLogEntry[] {
  try {
    const raw = localStorage.getItem(ERROR_HISTORY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.slice(-ERROR_HISTORY_LIMIT) : []
  } catch {
    return []
  }
}

function saveErrorHistory(entries: ErrorLogEntry[]): void {
  try {
    localStorage.setItem(ERROR_HISTORY_KEY, JSON.stringify(entries.slice(-ERROR_HISTORY_LIMIT)))
  } catch {
    // localStorage недоступен — молча игнорируем
  }
}

interface UIState {
  toasts: Toast[]
  sidebarOpen: boolean
  errorHistory: ErrorLogEntry[]
  addToast: (message: string, type?: Toast['type']) => void
  removeToast: (id: string) => void
  toggleSidebar: () => void
  logError: (message: string, source?: string) => void
  clearErrorHistory: () => void
}

let toastId = 0
let errorId = 0

export const useUIStore = create<UIState>((set, get) => ({
  toasts: [],
  sidebarOpen: true,
  errorHistory: loadErrorHistory(),

  addToast: (message, type = 'info') => {
    const id = String(++toastId)
    set((s) => ({ toasts: [...s.toasts, { id, message, type }] }))
    if (type === 'error') {
      get().logError(message, 'toast')
    }
    // Auto-dismiss after 4s
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
    }, 4000)
  },

  removeToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  toggleSidebar: () =>
    set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  logError: (message, source) => {
    const entry: ErrorLogEntry = {
      id: `${Date.now()}-${++errorId}`,
      message,
      timestamp: Date.now(),
      url: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
      source,
    }
    set((s) => {
      const next = [...s.errorHistory, entry].slice(-ERROR_HISTORY_LIMIT)
      saveErrorHistory(next)
      return { errorHistory: next }
    })
  },

  clearErrorHistory: () => {
    saveErrorHistory([])
    set({ errorHistory: [] })
  },
}))
